import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  PlusCircle, 
  Eye, 
  Briefcase, 
  TrendingUp, 
  Users, 
  Clock 
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function Jobs() {
  const { user, isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      {/* Header Section */}
      <div className="bg-surface shadow-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Briefcase className="w-12 h-12 text-primary" />
            </div>
            <h1 className="text-4xl font-bold text-secondary mb-2">
              Job Opportunities
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Connect with career opportunities and find your next role through our job portal
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Action Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Post Job Card */}
          <Card className="border-primary-light shadow-lg hover:shadow-xl transition-shadow duration-300">
            <CardHeader className="text-center pb-6">
              <div className="flex justify-center mb-4">
                <div className="bg-primary-light p-4 rounded-full">
                  <PlusCircle className="w-8 h-8 text-primary" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-secondary">
                Post a Job
              </CardTitle>
              <CardDescription className="text-gray-600 text-base">
                Share job opportunities with our community of skilled professionals
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-center space-x-2 text-gray-600">
                  <TrendingUp className="w-5 h-5" />
                  <span>Reach qualified candidates</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-gray-600">
                  <Users className="w-5 h-5" />
                  <span>Connect with our community</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-gray-600">
                  <Clock className="w-5 h-5" />
                  <span>Quick approval process</span>
                </div>
              </div>
              
              {isAuthenticated ? (
                <Link to="/jobs/post">
                  <Button 
                    className="w-full bg-primary hover:bg-primary-dark text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
                    size="lg"
                  >
                    Post New Job
                  </Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button 
                    className="w-full bg-primary hover:bg-primary-dark text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
                    size="lg"
                  >
                    Sign In to Post Job
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>

          {/* View Jobs Card */}
          <Card className="border-primary-light shadow-lg hover:shadow-xl transition-shadow duration-300">
            <CardHeader className="text-center pb-6">
              <div className="flex justify-center mb-4">
                <div className="bg-primary-light p-4 rounded-full">
                  <Eye className="w-8 h-8 text-primary" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-secondary">
                Browse Jobs
              </CardTitle>
              <CardDescription className="text-gray-600 text-base">
                Explore current job openings and find your next opportunity
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-center space-x-2 text-gray-600">
                  <Briefcase className="w-5 h-5" />
                  <span>Latest job postings</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-gray-600">
                  <TrendingUp className="w-5 h-5" />
                  <span>Filter by category & location</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-gray-600">
                  <Users className="w-5 h-5" />
                  <span>View detailed job descriptions</span>
                </div>
              </div>
              
              <Link to="/jobs/view">
                <Button 
                  className="w-full bg-secondary hover:bg-secondary-light text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
                  size="lg"
                >
                  Browse All Jobs
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* My Jobs Section - Only for authenticated users */}
        {isAuthenticated && (
          <div className="text-center">
            <Card className="border-border shadow-lg max-w-md mx-auto">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-secondary">
                  Manage Your Jobs
                </CardTitle>
                <CardDescription>
                  View and manage jobs you've posted
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/jobs/my-jobs">
                  <Button 
                    variant="outline" 
                    className="w-full border-border text-gray-700 hover:bg-gray-50"
                    size="lg"
                  >
                    View My Jobs
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-16 text-center">
          <div className="bg-surface rounded-lg shadow-sm p-8 max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-secondary mb-4">
              How It Works
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-primary-light rounded-full p-3 w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <span className="text-primary font-bold text-xl">1</span>
                </div>
                <h3 className="font-semibold text-secondary mb-2">Post Your Job</h3>
                <p className="text-gray-600 text-sm">
                  Create a detailed job posting with all requirements
                </p>
              </div>
              <div className="text-center">
                <div className="bg-primary-light rounded-full p-3 w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <span className="text-primary font-bold text-xl">2</span>
                </div>
                <h3 className="font-semibold text-secondary mb-2">Admin Review</h3>
                <p className="text-gray-600 text-sm">
                  Our team reviews and approves quality job postings
                </p>
              </div>
              <div className="text-center">
                <div className="bg-purple-100 rounded-full p-3 w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <span className="text-purple-600 font-bold text-xl">3</span>
                </div>
                <h3 className="font-semibold text-secondary mb-2">Get Discovered</h3>
                <p className="text-gray-600 text-sm">
                  Connect with qualified candidates from our community
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 